import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { getDefaultStage } from 'features/libra/lifecycle/lifecycleTypes'
import {
  FieldValues,
  TicketFieldValuesSchema,
  TicketInputProvenance,
  TicketInputProvenanceSchema,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  type LibraWorkflow,
  mapEntryToLibraWorkflow,
} from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoNotifications } from 'features/notifications'
import {
  generateEntrySlug,
  type RolodexEntryQueryBody,
  useRolodexEntries,
} from 'features/rolodex'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useCallback, useMemo } from 'react'
import {
  GeckoEntry,
  RolodexEntryPatchJson,
  RolodexEntryPutJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'
import { v4 } from 'uuid'
import z from 'zod'

export type TicketStatus = string

/**
 * Rolodex `libra-ticket-group` `data` key for TAR / campaign ROI inputs.
 * - Global (one row): `data['tar-roi'][TAR_ROI_GLOBAL_STORAGE_KEY]`
 * - Per turnaround / site: `data['tar-roi'][siteRolodexId]`
 * Legacy: older groups store all fields on each site id; the model still merges those.
 */
export const TAR_ROI_GROUP_DATA_KEY = 'tar-roi' as const

/** Reserved map key under `tar-roi` for assumptions shared across all sites in the group. */
export const TAR_ROI_GLOBAL_STORAGE_KEY = '__roi_global__' as const

/** Fields allowed under `tar-roi` (global row, site rows, or legacy combined rows). */
const tarRoiSiteValueSchema = z
  .object({
    /** @deprecated Prefer `hseIncidentProbPerPersonPerTar` (per person, per TAR). */
    hseIncidentProbPerPersonPerDay: z.number().optional(),
    hseIncidentProbPerPersonPerTar: z.number().optional(),
    hseDownsideAed: z.number().optional(),
    /** Percentage points, e.g. 5 = 5%. */
    criticalPathReductionPercent: z.number().optional(),
    niiRateAedPerM2: z.number().optional(),
    niiPeoplePerAsset: z.number().optional(),
    traditionalInspectionTeamPerAsset: z.number().optional(),
    equipmentPrepMobDemobAedPerAsset: z.number().optional(),
    traditionalInspectionAedPerAsset: z.number().optional(),
    niiInspectionAedPerAsset: z.number().optional(),
    scaffoldingAedPerAsset: z.number().optional(),
    /** NII inspection rate in USD per m², applied to blended NII (coverage) area. */
    niiInspectionRateUsdPerM2: z.number().optional(),
    /** Internal inspection rate in USD per m², applied to the approximate vessel size. */
    internalInspectionRateUsdPerM2: z.number().optional(),
    /** FX rate, AED per USD, used when inspection cost is computed from per-m² rates. */
    fxRateAedPerUsd: z.number().optional(),
    /** Extension avoidance: probability the TAR extends under traditional inspection (percentage points). */
    extensionProbBaselinePercent: z.number().optional(),
    /** Extension avoidance: probability the TAR extends with NII pre-discovery (percentage points). */
    extensionProbWithNiiPercent: z.number().optional(),
    /** Extension avoidance: expected length (days) of a TAR extension when it occurs. */
    expectedExtensionDays: z.number().optional(),
    /** Extension avoidance: confidence NII findings avoid the extension (percentage points). */
    discoveryConfidencePercent: z.number().optional(),
    /** @deprecated No longer used in ROI math. */
    tarHeadcount: z.number().optional(),
    tarDurationDays: z.number().optional(),
    /** @deprecated Replaced by `dailyRatePerPersonAed` (TAR-level). */
    mobDemobCostPerPersonAed: z.number().optional(),
    dailyRatePerPersonAed: z.number().optional(),
    dailyProductionAed: z.number().optional(),
    inspectionOnCriticalPath: z.boolean().optional(),
  })
  .strict()

const libraTicketGroupDataSchema = z.object({
  title: z.string().default('Untitled Group'),
  totalAssets: z.number().optional(),
  owners: z.array(z.string()).default([]),
  /**
   * Workflows allowed when creating tickets for this group.
   * Absent/empty → any org workflow (legacy). Length 1 → picker hidden.
   * Length > 1 → picker limited to these ids.
   */
  createWorkflowIds: z.array(z.string()).optional(),
  [TAR_ROI_GROUP_DATA_KEY]: z
    .record(z.string(), tarRoiSiteValueSchema)
    .optional(),
})
export type LibraTicketGroupData = z.infer<typeof libraTicketGroupDataSchema>

export type SiteRoiAssumptionsValue = z.infer<typeof tarRoiSiteValueSchema>
export type TarRoiSiteParams = SiteRoiAssumptionsValue

function migrateTicketGroupRawData(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') {
    return {}
  }
  const o = { ...(data as Record<string, unknown>) }
  if (
    (o[TAR_ROI_GROUP_DATA_KEY] === undefined ||
      o[TAR_ROI_GROUP_DATA_KEY] === null) &&
    o['siteRoiAssumptions'] !== undefined &&
    o['siteRoiAssumptions'] !== null
  ) {
    o[TAR_ROI_GROUP_DATA_KEY] = o['siteRoiAssumptions']
  }
  return o
}

export function parseLibraTicketGroupData(data: unknown): LibraTicketGroupData {
  return libraTicketGroupDataSchema.parse(migrateTicketGroupRawData(data))
}

export type LibraTicketGroup = LibraTicketGroupData & {
  id: string
  tags: RolodexEntryTagsType
  access_restriction: 'internal' | null
}

export interface LibraTicket {
  id: string
  workflowId: string
  title: string
  /**
   * Ticket-root field values. Top-level keys are ticket-global `FieldValue`s;
   * the reserved `components` key (see `TicketFieldValues`) holds a nested
   * `Record<componentId, Record<fieldKey, FieldValue>>` written by step
   * components mounted inside `<UnitComponentTabs>` via
   * `useScopedFieldAccess({ kind: 'component', ... })`.
   */
  fieldValues: FieldValues
  /**
   * Annotation-only provenance overlay: `fieldPath -> FieldValueSource`.
   * Records where each FFS input value came from; the values themselves live
   * on the asset-model snapshot / ffs-configuration entry. See
   * `stepComponents/ffs/inputProvenance.ts` for the key convention.
   */
  inputProvenance: TicketInputProvenance
  stepId: string
  workflowComplete: boolean
  version: number
  assessmentId: string
  completedBy: string | null
  createdBy: string | null
  lastUpdatedBy: string | null
  versionJustification?: string
  createdAt?: string
  updatedAt?: string
  tags: RolodexEntryTagsType
  ticketStatus: string
  terminalTransitionBy: string | null
  terminalTransitionAt: string | null
  submittedForReviewBy: string | null
  submittedForReviewAt: string | null
  contributors: string[]
}

const libraTicketDataObjectSchema = z.object({
  title: z.string(),
  stepId: z.string(),
  workflowComplete: z.boolean().optional().default(false),
  version: z.number().optional().default(1),
  assessmentId: z.string().optional(),
  completedBy: z.string().nullable().optional().default(null),
  versionJustification: z.string().optional(),
  ticketStatus: z.string().default(''),
  terminalTransitionBy: z.string().nullable().optional().default(null),
  terminalTransitionAt: z.string().nullable().optional().default(null),
  submittedForReviewBy: z.string().nullable().optional().default(null),
  submittedForReviewAt: z.string().nullable().optional().default(null),
  contributors: z.array(z.string()).default([]),
  inputProvenance: TicketInputProvenanceSchema.default({}),
})

const buildLibraTicketDataSchema = (defaultStepId: string) =>
  libraTicketDataObjectSchema.extend({
    // Validate the ticket-root shape at parse time so a malformed
    // `fieldValues.components` (e.g., a scalar where a nested map should be,
    // a component entry whose value isn't a `Record<fieldKey, FieldValue>`)
    // fails the load rather than silently reaching consumers as `unknown`.
    // The schema is intentionally permissive at the leaf `FieldValue` level
    // (`value: z.unknown()` accepts missing keys) so legacy non-FFS tickets
    // — never carry a `components` key and only have flat FieldValue slots —
    // continue to parse.
    fieldValues: TicketFieldValuesSchema.default({}),
    stepId: z.string().default(defaultStepId),
  })

type TicketDataSchema = ReturnType<typeof buildLibraTicketDataSchema>

const ticketDataSchemaByDefaultStepId = new Map<string, TicketDataSchema>()

/**
 * Cached on `defaultStepId`, the schema's only input. Callers build one per
 * ticket while `.extend()` allocates a fresh schema every call, and the key
 * set is one entry per workflow.
 */
const createLibraTicketDataSchema = (
  defaultStepId: string
): TicketDataSchema => {
  const cached = ticketDataSchemaByDefaultStepId.get(defaultStepId)
  if (cached) return cached

  const schema = buildLibraTicketDataSchema(defaultStepId)
  ticketDataSchemaByDefaultStepId.set(defaultStepId, schema)
  return schema
}

/**
 * Builds the validated `data` payload for a new ticket. Single source of
 * truth for ticket-creation defaults — `useCreateTicket` (hook) and
 * `createLibraTicket` (non-hook) both call this so schema changes (e.g.
 * adding a new defaulted field) update both creation paths atomically.
 */
export function buildCreateTicketData({
  workflow,
  title,
  initialData,
}: {
  workflow: LibraWorkflow
  title: string
  initialData?: {
    fieldValues?: Record<string, unknown>
    ticketStatus?: string
  }
}): Record<string, unknown> {
  const defaultStageId = workflow.lifecycleConfig
    ? (getDefaultStage(workflow.lifecycleConfig)?.id ?? '')
    : ''
  const firstStepId = first(workflow.steps)?.id ?? ''

  // Parse through the schema so every defaulted field is populated
  // consistently. We pass an `assessmentId` because the schema marks it
  // optional but creation needs one assigned.
  return createLibraTicketDataSchema(firstStepId).parse({
    title,
    fieldValues: initialData?.fieldValues ?? {},
    assessmentId: v4(),
    ticketStatus: initialData?.ticketStatus ?? defaultStageId,
  })
}

const mapEntryToLibraTicket = (
  entry: GeckoEntry,
  parseTicket: (data: GeckoEntry['data']) => {
    title: string
    fieldValues: FieldValues
    inputProvenance: TicketInputProvenance
    stepId: string
    workflowComplete: boolean
    version: number
    assessmentId?: string
    completedBy: string | null
    versionJustification?: string
    ticketStatus: string
    terminalTransitionBy: string | null
    terminalTransitionAt: string | null
    submittedForReviewBy: string | null
    submittedForReviewAt: string | null
    contributors: string[]
  }
): LibraTicket | undefined => {
  const workflowId = entry.tags[RolodexEntryType.LIBRA_WORKFLOW]
  if (!workflowId) {
    return undefined
  }

  const parsedData = parseTicket({ ...entry.data })

  return {
    id: entry.id,
    title: parsedData.title,
    fieldValues: parsedData.fieldValues,
    inputProvenance: parsedData.inputProvenance,
    stepId: parsedData.stepId,
    workflowComplete: parsedData.workflowComplete,
    version: parsedData.version,
    assessmentId: parsedData.assessmentId ?? entry.id,
    completedBy: parsedData.completedBy,
    versionJustification: parsedData.versionJustification,
    createdBy: entry.raw.created_by ?? null,
    lastUpdatedBy: entry.raw.last_updated_by ?? null,
    // Read from `raw` like the two fields above: the rolodex dispenser's
    // fallback for entry types without a Gecko class (libra-ticket is one)
    // copies `updated_at` but not `created_at`, so the class property is
    // always undefined here.
    createdAt: entry.raw.created_at ?? '',
    updatedAt: entry.updated_at ?? '',
    workflowId,
    tags: entry.tags,
    ticketStatus: parsedData.ticketStatus,
    terminalTransitionBy: parsedData.terminalTransitionBy,
    terminalTransitionAt: parsedData.terminalTransitionAt,
    submittedForReviewBy: parsedData.submittedForReviewBy,
    submittedForReviewAt: parsedData.submittedForReviewAt,
    contributors: parsedData.contributors,
  }
}

const mapEntryToLibraTicketGroup = (entry: GeckoEntry): LibraTicketGroup => {
  const parsedData = parseLibraTicketGroupData({ ...entry.data })

  return {
    id: entry.id,
    ...parsedData,
    tags: entry.tags,
    access_restriction: entry.access_restriction,
  }
}

export function parseLibraTicketGroupEntry(
  entry: GeckoEntry
): LibraTicketGroup {
  return mapEntryToLibraTicketGroup(entry)
}

export type UpdateTicketArgs = {
  ticketId: string
  stepId?: string
  /**
   * Whole-ticket fieldValues replacement. Includes the reserved `components`
   * nested map when the caller wants to update per-component values via the
   * scoped-write path in `useScopedFieldAccess({ kind: 'component' })`.
   */
  fieldValues?: FieldValues
  /** Whole-map replacement of the annotation-only provenance overlay. */
  inputProvenance?: TicketInputProvenance
  workflowComplete?: boolean
  completedBy?: string | null
  ticketStatus?: string
  terminalTransitionBy?: string | null
  terminalTransitionAt?: string | null
  submittedForReviewBy?: string | null
  submittedForReviewAt?: string | null
  contributors?: string[]
}

/**
 * Every ticket field an `UpdateTicketArgs` patch may carry (everything but
 * the `ticketId` routing key). Single source of truth shared by
 * `buildTicketUpdatePatches` (Rolodex patch construction) and
 * `TicketWriterContext`'s `applyPatchToTicket` (optimistic snapshot merge):
 * adding a field to `UpdateTicketArgs` without listing it here is a type
 * error, so the two sides cannot silently drift.
 */
export const TICKET_UPDATE_FIELD_KEYS = [
  'stepId',
  'fieldValues',
  'inputProvenance',
  'workflowComplete',
  'completedBy',
  'ticketStatus',
  'terminalTransitionBy',
  'terminalTransitionAt',
  'submittedForReviewBy',
  'submittedForReviewAt',
  'contributors',
] as const satisfies ReadonlyArray<keyof Omit<UpdateTicketArgs, 'ticketId'>>

// Compile-time completeness check: a key of UpdateTicketArgs missing from
// TICKET_UPDATE_FIELD_KEYS makes this alias non-assignable.
type AssertKeysExhaustive = keyof Omit<
  UpdateTicketArgs,
  'ticketId'
> extends (typeof TICKET_UPDATE_FIELD_KEYS)[number]
  ? true
  : never
const _ticketUpdateKeysExhaustive: AssertKeysExhaustive = true
void _ticketUpdateKeysExhaustive

type CreateTicketVersionArgs = {
  sourceTicket: LibraTicket
  versionJustification?: string
}

type CreateGroupArgs = {
  title: string
  organization?: string | null
  site?: string | null
  unit?: string | null
  isInternal?: boolean
}

/** The next version of `sourceTicket`: same data, restarted in its lifecycle. */
export function buildTicketVersion(
  id: string,
  { sourceTicket, versionJustification }: CreateTicketVersionArgs
): LibraTicket {
  return {
    // Deep, so nested field values such as `components` aren't shared with the source.
    ...structuredClone(sourceTicket),
    id,
    workflowComplete: false,
    version: sourceTicket.version + 1,
    assessmentId: sourceTicket.assessmentId || sourceTicket.id,
    completedBy: null,
    lastUpdatedBy: null,
    versionJustification,
    createdAt: undefined,
    updatedAt: undefined,
    ticketStatus: '',
    terminalTransitionBy: null,
    terminalTransitionAt: null,
    submittedForReviewBy: null,
    submittedForReviewAt: null,
    contributors: [],
  }
}

export function useCreateTicketVersion(): (
  args: CreateTicketVersionArgs
) => Promise<string> {
  const { rolodexPut } = useRolodexEdit()

  return useCallback(
    async (args: CreateTicketVersionArgs) => {
      const id = generateEntrySlug('ticket', v4().slice(0, 6))
      await rolodexPut([entryFromLibraTicket(buildTicketVersion(id, args))])
      return id
    },
    [rolodexPut]
  )
}

export const entryFromLibraTicket = (
  ticket: LibraTicket
): RolodexEntryPutJson => {
  return {
    id: ticket.id,
    type: RolodexEntryType.LIBRA_TICKET,
    data: {
      title: ticket.title,
      stepId: ticket.stepId,
      workflowComplete: ticket.workflowComplete,
      fieldValues: ticket.fieldValues,
      inputProvenance: ticket.inputProvenance,
      version: ticket.version,
      assessmentId: ticket.assessmentId,
      completedBy: ticket.completedBy,
      versionJustification: ticket.versionJustification,
      ticketStatus: ticket.ticketStatus,
      terminalTransitionBy: ticket.terminalTransitionBy,
      terminalTransitionAt: ticket.terminalTransitionAt,
      submittedForReviewBy: ticket.submittedForReviewBy,
      submittedForReviewAt: ticket.submittedForReviewAt,
      contributors: ticket.contributors,
    },
    tags: {
      ...ticket.tags,
      [RolodexEntryType.LIBRA_WORKFLOW]: ticket.workflowId,
    },
  }
}

export function useCreateGroup(): (args: CreateGroupArgs) => Promise<string> {
  const { rolodexPut } = useRolodexEdit()
  const workflow = useWorkflow()
  return useCallback(
    async ({ title, organization }: CreateGroupArgs) => {
      const group = libraTicketGroupDataSchema.parse({
        title,
      })
      const id = generateEntrySlug('ticket-group', v4().slice(0, 6))
      const tags: RolodexEntryTagsType = {
        [RolodexEntryType.LIBRA_WORKFLOW]: workflow.id,
      }

      if (organization) {
        tags.organization = organization
      }

      await rolodexPut([
        {
          id,
          type: RolodexEntryType.LIBRA_TICKET_GROUP,
          data: group,
          tags,
        },
      ])
      return id
    },
    [rolodexPut, workflow]
  )
}

// Dashboard version - creates group without workflow context
export function useCreateGroupForDashboard(): (
  args: CreateGroupArgs
) => Promise<string> {
  const { rolodexPut, rolodexPatchAccessRestriction } = useRolodexEdit()
  return useCallback(
    async ({ title, organization, site, isInternal }: CreateGroupArgs) => {
      const group = libraTicketGroupDataSchema.parse({
        title,
      })
      const id = generateEntrySlug('ticket-group', v4().slice(0, 6))
      const tags: RolodexEntryTagsType = {}

      if (organization) {
        tags.organization = organization
      }
      if (site) {
        tags.site = site
      }

      await rolodexPut([
        {
          id,
          type: RolodexEntryType.LIBRA_TICKET_GROUP,
          data: group,
          tags,
        },
      ])

      if (isInternal) {
        await rolodexPatchAccessRestriction({
          restriction: 'internal',
          entry_ids: [id],
        })
      }

      return id
    },
    [rolodexPut, rolodexPatchAccessRestriction]
  )
}

/**
 * Patches a libra-ticket-group entry's `data` (e.g. site-scoped ROI assumptions).
 */
export function useUpdateLibraTicketGroup(): (args: {
  group: LibraTicketGroup
  next: LibraTicketGroupData
}) => Promise<void> {
  const { rolodexPatch } = useRolodexEdit()
  return useCallback(
    async ({ group, next }) => {
      const from = libraTicketGroupDataSchema.parse({
        title: group.title,
        totalAssets: group.totalAssets,
        owners: group.owners,
        [TAR_ROI_GROUP_DATA_KEY]: group[TAR_ROI_GROUP_DATA_KEY],
      })
      const to = libraTicketGroupDataSchema.parse(next)
      const patch = createPatchData(
        group.id,
        from as Record<string, unknown>,
        to as Record<string, unknown>
      )
      if (patch) {
        await rolodexPatch([patch])
      }
    },
    [rolodexPatch]
  )
}

function buildWorkflowDefaultStepMap(
  entries: ReadonlyArray<GeckoEntry>
): ReadonlyMap<string, string> {
  const workflows = entries
    .filter((entry) => entry.type === RolodexEntryType.LIBRA_WORKFLOW)
    .map(mapEntryToLibraWorkflow)
  return workflows.reduce((acc, workflow) => {
    const firstStep = first(workflow.steps)
    return firstStep ? new Map(acc).set(workflow.id, firstStep.id) : acc
  }, new Map<string, string>())
}

function buildTicketSchema(
  entry: GeckoEntry,
  stepMap: ReadonlyMap<string, string>
): TicketDataSchema | undefined {
  const workflowId = entry.tags[RolodexEntryType.LIBRA_WORKFLOW]
  if (!workflowId) return undefined
  return createLibraTicketDataSchema(stepMap.get(workflowId) ?? '')
}

function parseTicketEntry(
  entry: GeckoEntry,
  schema: TicketDataSchema
): LibraTicket | undefined {
  try {
    return mapEntryToLibraTicket(entry, (data) => {
      const parsed = schema.parse(data)
      return {
        ...parsed,
        // The schema keeps fieldValues loose (`z.record(z.string(),
        // z.unknown())`) to avoid regressing legacy tickets that predate the
        // strict `TicketFieldValuesSchema`. Cast here at the boundary so
        // downstream code can rely on the discriminated ticket shape.
        fieldValues: parsed.fieldValues as FieldValues,
      }
    })
  } catch (error) {
    console.error(
      `Failed to parse ticket data for entry ${entry.id}:`,
      error,
      entry
    )
    return undefined
  }
}

/**
 * Parse a libra-ticket Rolodex entry into a typed `LibraTicket` without
 * needing a workflow stepMap. Read-only callers (analytics, dashboards) that
 * just want the typed shape can use this directly; `stepId` falls back to
 * `''` when the entry omits it. Write paths must keep using the
 * `useLibraTickets`/`buildTicketSchema` flow so workflow-default stepIds are
 * honored.
 */
export function parseLibraTicketEntry(
  entry: GeckoEntry
): LibraTicket | undefined {
  return parseTicketEntry(entry, createLibraTicketDataSchema(''))
}

function buildTicketUpdatePatches(
  ticketId: string,
  entry: GeckoEntry,
  schema: TicketDataSchema,
  args: Omit<UpdateTicketArgs, 'ticketId'>
): RolodexEntryPatchJson[] | undefined {
  const ticketData = schema.parse(entry.data) as Record<string, unknown>

  const from: Record<string, unknown> = {}
  const to: Record<string, unknown> = {}

  for (const key of TICKET_UPDATE_FIELD_KEYS) {
    const nextValue = args[key]
    if (nextValue === undefined) continue
    from[key] = ticketData[key]
    to[key] = nextValue
  }

  const patch = createPatchData(ticketId, from, to)
  return patch ? [patch] : undefined
}

/** In-progress tickets first, then most recently created. */
function compareTicketsForDisplay(a: LibraTicket, b: LibraTicket): number {
  if (a.workflowComplete !== b.workflowComplete) {
    return a.workflowComplete ? 1 : -1
  }
  return b.createdAt?.localeCompare(a.createdAt ?? '') ?? 0
}

export const useLibraTickets = (
  tags: RolodexEntryTagsType | Record<string, string[]>
): {
  tickets: ReadonlyArray<LibraTicket>
  groups: ReadonlyArray<LibraTicketGroup>
  ticketGroups: ReadonlyMap<string, LibraTicketGroup>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => Promise<void>
  doUpdateTicket: (arg: UpdateTicketArgs) => Promise<void>
} => {
  const queryBody = useMemo(() => {
    return {
      type: RolodexEntryType.LIBRA_TICKET,
      links: [
        RolodexEntryType.LIBRA_TICKET_GROUP,
        RolodexEntryType.LIBRA_WORKFLOW,
      ],
      tags,
    }
  }, [tags])

  const { entries, isLoading, hasEverLoaded, forceRefresh } = useRolodexEntries(
    queryBody,
    {
      swrOptions: {
        compare: isEqual,
        revalidateOnFocus: false,
      },
    }
  )

  const stepMap = useMemo(
    () => buildWorkflowDefaultStepMap(entries ?? []),
    [entries]
  )

  const { ticketEntries, groupEntries } = useMemo(
    () => ({
      ticketEntries: (entries ?? []).filter(
        (entry) => entry.type === RolodexEntryType.LIBRA_TICKET
      ),
      groupEntries: (entries ?? []).filter(
        (entry) => entry.type === RolodexEntryType.LIBRA_TICKET_GROUP
      ),
    }),
    [entries]
  )
  const { errorNotification } = useGeckoNotifications()
  const { rolodexPatch } = useRolodexEdit()

  const getSchema = useCallback(
    (entry: GeckoEntry) => {
      const schema = buildTicketSchema(entry, stepMap)
      if (!schema) {
        errorNotification(
          `Unable to construct ticket schema for ticket ${entry.id}`
        )
        console.error(
          `Unable to construct ticket schema for ticket ${entry.id}`
        )
      }
      return schema
    },
    [stepMap, errorNotification]
  )

  const doUpdateTicket = useCallback(
    async ({ ticketId, ...rest }: UpdateTicketArgs) => {
      const entry = ticketEntries?.find((e) => e.id === ticketId)
      if (!entry) {
        console.warn(`Unknown ticket id: ${ticketId}`)
        return
      }
      const schema = getSchema(entry)
      if (!schema) return

      const patches = buildTicketUpdatePatches(ticketId, entry, schema, rest)
      if (!patches) {
        console.warn(`No changes to ticket ${ticketId}`)
        return
      }
      await rolodexPatch(patches)
      await forceRefresh()
    },
    [ticketEntries, getSchema, rolodexPatch, forceRefresh]
  )

  return useMemo(() => {
    const sortedTickets =
      ticketEntries
        .map((entry) => {
          const schema = getSchema(entry)
          if (!schema) return undefined
          return parseTicketEntry(entry, schema)
        })
        .filter((ticket): ticket is LibraTicket => ticket !== undefined)
        .toSorted(compareTicketsForDisplay) ?? []

    const groups = groupEntries.map((entry) =>
      mapEntryToLibraTicketGroup(entry)
    )

    const groupsMap = new Map(groups.map((group) => [group.id, group]))

    return {
      tickets: sortedTickets,
      ticketGroups: groupsMap,
      groups,
      isLoading,
      hasEverLoaded,
      forceRefresh,
      doUpdateTicket,
    }
  }, [
    doUpdateTicket,
    forceRefresh,
    getSchema,
    groupEntries,
    hasEverLoaded,
    isLoading,
    ticketEntries,
  ])
}

/**
 * Read-only ticket list. Omits the links `useLibraTickets` requests, which
 * inline a copy of the workflow and group entries on every ticket in the
 * response. Callers need their own workflows and must not write tickets.
 */
export const useDashboardLibraTickets = (
  tags: RolodexEntryTagsType | Record<string, string[]>
): {
  tickets: ReadonlyArray<LibraTicket>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => Promise<void>
} => {
  const queryBody = useMemo(
    () => ({ type: RolodexEntryType.LIBRA_TICKET, tags }),
    [tags]
  )

  const { entries, isLoading, hasEverLoaded, forceRefresh } = useRolodexEntries(
    queryBody,
    {
      swrOptions: {
        compare: isEqual,
        revalidateOnFocus: false,
      },
    }
  )

  const tickets = useMemo(
    () =>
      (entries ?? [])
        .map(parseLibraTicketEntry)
        .filter((ticket): ticket is LibraTicket => ticket !== undefined)
        .toSorted(compareTicketsForDisplay),
    [entries]
  )

  return useMemo(
    () => ({ tickets, isLoading, hasEverLoaded, forceRefresh }),
    [tickets, isLoading, hasEverLoaded, forceRefresh]
  )
}

export const useLibraTicket = (
  ticketId: string | undefined
): {
  ticket: LibraTicket | undefined
  isLoading: boolean
  hasEverLoaded: boolean
  doUpdateTicket: (arg: UpdateTicketArgs) => Promise<void>
} => {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    if (!ticketId) return undefined
    return {
      ids: [ticketId],
      type: RolodexEntryType.LIBRA_TICKET,
      links: [RolodexEntryType.LIBRA_WORKFLOW],
    }
  }, [ticketId])

  const { entries, isLoading, hasEverLoaded, forceRefresh } = useRolodexEntries(
    queryBody,
    {
      swrOptions: {
        compare: isEqual,
        revalidateOnFocus: false,
      },
    }
  )

  const stepMap = useMemo(
    () => buildWorkflowDefaultStepMap(entries ?? []),
    [entries]
  )

  const ticketEntry = useMemo(() => {
    return (entries ?? []).find(
      (entry) => entry.type === RolodexEntryType.LIBRA_TICKET
    )
  }, [entries])

  const { errorNotification } = useGeckoNotifications()
  const { rolodexPatch } = useRolodexEdit()

  const getSchema = useCallback(
    (entry: GeckoEntry) => {
      const schema = buildTicketSchema(entry, stepMap)
      if (!schema) {
        errorNotification(
          `Unable to construct ticket schema for ticket ${entry.id}`
        )
        console.error(
          `Unable to construct ticket schema for ticket ${entry.id}`
        )
      }
      return schema
    },
    [stepMap, errorNotification]
  )

  const doUpdateTicket = useCallback(
    async ({ ticketId: updateTicketId, ...rest }: UpdateTicketArgs) => {
      if (!ticketEntry || ticketEntry.id !== updateTicketId) {
        console.warn(`Unknown ticket id: ${updateTicketId}`)
        return
      }
      const schema = getSchema(ticketEntry)
      if (!schema) return

      const patches = buildTicketUpdatePatches(
        updateTicketId,
        ticketEntry,
        schema,
        rest
      )
      if (!patches) {
        console.warn(`No changes to ticket ${updateTicketId}`)
        return
      }
      await rolodexPatch(patches)
      await forceRefresh()
    },
    [ticketEntry, getSchema, rolodexPatch, forceRefresh]
  )

  const ticket = useMemo<LibraTicket | undefined>(() => {
    if (!ticketEntry) return undefined
    const schema = getSchema(ticketEntry)
    if (!schema) return undefined
    return parseTicketEntry(ticketEntry, schema)
  }, [ticketEntry, getSchema])

  return useMemo(
    () => ({
      ticket,
      isLoading,
      hasEverLoaded,
      doUpdateTicket,
    }),
    [ticket, isLoading, hasEverLoaded, doUpdateTicket]
  )
}

export function useLibraWorkflows({
  ids,
  tags,
}: {
  ids?: ReadonlyArray<string>
  tags?: RolodexEntryTagsType
}): {
  libraWorkflows: ReadonlyArray<LibraWorkflow>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    const hasIds = ids && !isEmpty(ids)
    if (!hasTags && !hasIds) {
      return undefined
    }

    return {
      type: RolodexEntryType.LIBRA_WORKFLOW,
      ...(hasTags ? { tags } : {}),
      ...(hasIds ? { ids: [...ids] } : {}),
    }
  }, [ids, tags])

  const {
    entries: libraWorkflows,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryBody, {
    swrOptions: {
      compare: isEqual,
      revalidateOnFocus: false,
    },
  })

  return useMemo(
    () => ({
      libraWorkflows: (libraWorkflows ?? []).map(mapEntryToLibraWorkflow),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [forceRefresh, hasEverLoaded, isLoading, libraWorkflows]
  )
}

export function useLibraGroups({
  ids,
  tags,
}: {
  ids?: ReadonlyArray<string>
  tags?: RolodexEntryTagsType | Record<string, string[]>
}): {
  groups: ReadonlyArray<LibraTicketGroup>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    const hasIds = ids && !isEmpty(ids)
    if (!hasTags && !hasIds) {
      return undefined
    }

    return {
      type: RolodexEntryType.LIBRA_TICKET_GROUP,
      ...(hasTags ? { tags } : {}),
      ...(hasIds ? { ids: [...ids] } : {}),
    }
  }, [ids, tags])

  const { entries, isLoading, hasEverLoaded, forceRefresh } = useRolodexEntries(
    queryBody,
    {
      swrOptions: {
        compare: isEqual,
        revalidateOnFocus: false,
      },
    }
  )

  return useMemo(
    () => ({
      groups: (entries ?? []).map((entry) => mapEntryToLibraTicketGroup(entry)),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [entries, forceRefresh, hasEverLoaded, isLoading]
  )
}
